/**
 * On-screen controls and the toggle buttons in the masthead.
 *
 * Every control works with a mouse, a finger, and the keyboard. The hold
 * buttons repeat while pressed; the fire button auto-repeats so the laser
 * behaves like the arcade cabinet's fire button.
 */

const REPEAT_MS = 70;

export function createControls({ root, input, onFire, toggles }) {
  const cleanups = [];
  const on = (el, type, handler, opts) => {
    el.addEventListener(type, handler, opts);
    cleanups.push(() => el.removeEventListener(type, handler, opts));
  };

  root.querySelectorAll('[data-hold]').forEach((button) => {
    const which = button.dataset.hold;

    const press = (event) => {
      button.setPointerCapture?.(event.pointerId);
      button.classList.add('is-pressed');
      input.setButton(which, true);
      event.preventDefault();
    };
    const release = () => {
      button.classList.remove('is-pressed');
      input.setButton(which, false);
    };

    on(button, 'pointerdown', press);
    on(button, 'pointerup', release);
    on(button, 'pointercancel', release);
    on(button, 'pointerleave', release);

    // Keyboard users focused on the button get the same hold behaviour.
    on(button, 'keydown', (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      event.preventDefault();
      button.classList.add('is-pressed');
      input.setButton(which, true);
    });
    on(button, 'keyup', (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      release();
    });
    on(button, 'blur', release);
  });

  const fireButton = root.querySelector('[data-fire]');
  if (fireButton) {
    let repeat = 0;

    const startFire = (event) => {
      fireButton.setPointerCapture?.(event?.pointerId);
      fireButton.classList.add('is-pressed');
      onFire();
      window.clearInterval(repeat);
      repeat = window.setInterval(onFire, REPEAT_MS);
      event?.preventDefault();
    };
    const stopFire = () => {
      fireButton.classList.remove('is-pressed');
      window.clearInterval(repeat);
      repeat = 0;
    };

    on(fireButton, 'pointerdown', startFire);
    on(fireButton, 'pointerup', stopFire);
    on(fireButton, 'pointercancel', stopFire);
    on(fireButton, 'pointerleave', stopFire);
    on(fireButton, 'keydown', (event) => {
      if (event.repeat) return;
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      event.preventDefault();
      startFire();
    });
    on(fireButton, 'keyup', (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      stopFire();
    });
    on(fireButton, 'blur', stopFire);
  }

  const findEl = (selector) =>
    root.closest?.('.arkanoid-app')?.querySelector(selector) ||
    root.querySelector(selector) ||
    document.querySelector(selector);

  for (const [selector, handler] of Object.entries(toggles || {})) {
    const button = findEl(selector);
    if (!button) continue;
    on(button, 'click', () => {
      const next = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(next));
      handler(next);
    });
  }

  return {
    setToggle(selector, value) {
      const button = findEl(selector);
      if (button) button.setAttribute('aria-pressed', String(value));
    },
    destroy() {
      cleanups.forEach((off) => off());
      cleanups.length = 0;
    },
  };
}
